/**
 * Proving the second factor again, mid-session, without losing anything.
 *
 * THE PROBLEM. A password session here must re-prove its second factor every
 * two hours (`needs2FA` in server/twoFactor.ts); until it does, every /api
 * route answers 403 `2FA_REQUIRED`. The client used to meet that with a full
 * page load to /verify — so somebody two hours into a long worksheet pressed
 * Save, the page navigated away, and everything typed into the form was gone.
 * A security control that eats people's work is one they learn to route
 * around.
 *
 * So instead: a promise, resolved by a dialog. `requestStepUp()` raises the
 * prompt, waits, and answers "verified" once the server has accepted a factor —
 * and `apiRequest` then retries the ORIGINAL call with the same body. Nothing is
 * retyped and nothing navigates.
 *
 * The dialog (`components/StepUpDialog.tsx`) registers itself here when it
 * mounts; nothing else needs to know it exists. With no dialog mounted this
 * answers "unavailable" and the caller falls back to the old full-page /verify
 * route, so the app is never worse off than before.
 */

export type StepUpOutcome =
  /** The server accepted a factor — retry the original request. */
  | "verified"
  /** The user chose "Not now", or recently did (see DISMISS_COOLDOWN_MS). */
  | "dismissed"
  /** No dialog is mounted to ask. The caller should fall back to /verify. */
  | "unavailable"
  /** A full-page second-factor screen is already on screen; do not stack a dialog on it. */
  | "suppressed";

type Resolver = (outcome: StepUpOutcome) => void;

/** Set while a prompt is on screen, so ten parallel 403s raise ONE dialog. */
let pending: { promise: Promise<StepUpOutcome>; resolve: Resolver } | null = null;

/** The listener the dialog registers. Null when nothing is mounted to show it. */
let onPrompt: (() => void) | null = null;

/** How many full-page second-factor screens are mounted (login card, /verify). */
let suppressors = 0;

/**
 * After "Not now", background reads stay quiet for this long.
 *
 * Without it, this app's polls (inventory every few seconds, notifications,
 * the badge) would each re-open the dialog the instant it was dismissed — a
 * prompt nobody can get rid of. A WRITE still asks every time: pressing Save is
 * somebody asking for the thing that needs the factor.
 */
export const DISMISS_COOLDOWN_MS = 60_000;
let dismissedAt = 0;

/** Called by the dialog component when it mounts. Returns its own teardown. */
export function registerStepUpPrompt(handler: () => void): () => void {
  onPrompt = handler;
  return () => {
    if (onPrompt === handler) onPrompt = null;
  };
}

/**
 * A full-page second-factor screen is showing (the sign-in card's inline
 * factor, or the /verify fallback page). Requests fired underneath it — the
 * service banner, a translation chunk — must not stack a dialog on top of the
 * very screen that is already asking. Returns the release.
 */
export function suppressStepUp(): () => void {
  suppressors += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    suppressors = Math.max(0, suppressors - 1);
  };
}

/**
 * Ask the user to prove a second factor.
 *
 * `interactive` is true for a write the user just asked for; false for a
 * background read. Only the second is held back by the dismiss cooldown.
 *
 * Resolves rather than hanging when no dialog is mounted: a poll firing on a
 * screen with no UI must not leave a promise outstanding for ever.
 */
export function requestStepUp(opts: { interactive: boolean }): Promise<StepUpOutcome> {
  if (suppressors > 0) return Promise.resolve("suppressed");
  if (pending) return pending.promise;
  if (!onPrompt) return Promise.resolve("unavailable");
  if (!opts.interactive && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) {
    return Promise.resolve("dismissed");
  }

  let resolve!: Resolver;
  const promise = new Promise<StepUpOutcome>((r) => {
    resolve = r;
  });
  pending = { promise, resolve };
  onPrompt();
  return promise;
}

/**
 * Called by the dialog with the outcome. Clears the shared state first so a
 * resolver that re-enters (a retried request meeting another 403) cannot find a
 * stale prompt still registered.
 */
export function settleStepUp(outcome: "verified" | "dismissed"): void {
  const p = pending;
  pending = null;
  // Nothing was asked (the dialog unmounting with no prompt open): nothing to
  // record, and no cooldown to start.
  if (!p) return;
  if (outcome === "dismissed") dismissedAt = Date.now();
  else dismissedAt = 0;
  p?.resolve(outcome);
}

/** Whether a prompt is currently outstanding — the dialog's open state. */
export function stepUpPending(): boolean {
  return pending !== null;
}

/** Test seam: forget everything. */
export function resetStepUpForTests(): void {
  pending = null;
  onPrompt = null;
  suppressors = 0;
  dismissedAt = 0;
}
